import { ColumnDef, flexRender, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';
import React from 'react';
import PageButton from '../forms/PageButton';
import { useTranslation } from 'react-i18next';
import { PageRequest } from '../../api/api.types';

interface ListViewTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  getTrProps?: (row: any) => void;
  sortBy?: (columnName: string, direction: 'ASC' | 'DESC') => void;
  sortedBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  paginationEnabled?: boolean;
  selectedPageNumber?: number;
  totalPages?: number;
  totalElements?: number;
  getDataFn?: (pageRequest: PageRequest) => void;
  onCellClickFn?: (cell: any) => void;

}

export default function ListViewTable<T>(
  {
    data,
    columns,
    getTrProps,
    sortBy,
    sortOrder,
    paginationEnabled,
    sortedBy,
    selectedPageNumber,
    totalPages,
    totalElements,
    getDataFn,
    onCellClickFn
  }: Readonly<ListViewTableProps<T>>) {
  const reactTable = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableColumnFilters: true,
    getFilteredRowModel: getFilteredRowModel()
  });

  const { t } = useTranslation();

  const getThClassname = (headerId: string) => {
    if (headerId === sortedBy) {
      return sortOrder === 'ASC' ? '-sort-asc' : '-sort-desc';
    }
    return '';
  };

  const composeRowCells = (row: any) => {
    return (
      row.getVisibleCells().map((cell: any) => {
        return (
          <td
            onClick={() => onCellClick(cell)}
            key={cell.id}
            {...{
              style: {
                textAlign: (cell.column.columnDef.meta)?.align,
                maxWidth: (cell.column.columnDef.meta)?.maxWidth,
                minWidth: (cell.column.columnDef.meta)?.minWidth,
                width: (cell.column.columnDef.meta)?.width
              }
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })
    );
  };

  const onColumnHeaderClick = (headerId: string) => {
    if (sortBy && selectedPageNumber)
      sortBy(headerId, sortOrder === 'ASC' ? 'DESC' : 'ASC');
  };

  const onRowClick = (row: any) => {
    if (getTrProps)
      getTrProps(row);
  };

  const onCellClick = (cell: any) => {
    if (onCellClickFn)
      onCellClickFn(cell);
  };

  const composeHeaderCells = (headerGroup: any) => {
    return (headerGroup.headers.map((header: any) => (
      <th key={header.id}
          className={`${getThClassname(header.id)} ${(header.column.columnDef.meta)?.numeric ? 'numeric-column' : ''}`}
          {...{
            onClick: () => onColumnHeaderClick(header.id),
            title: header.column.columnDef.headerTooltip,
            style: {
              textAlign: (header.column.columnDef.meta)?.align,
              maxWidth: (header.column.columnDef.meta)?.maxWidth,
              minWidth: (header.column.columnDef.meta)?.minWidth,
              width: (header.column.columnDef.meta)?.width
            }
          }}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </th>
    )));
  };

  const composeTableHead = (table: any) => {
    return (
      table.getHeaderGroups().map((headerGroup: any) => (
        <tr key={headerGroup.id}>
          {composeHeaderCells(headerGroup)}
        </tr>
      ))
    );
  };

  const getPageButtons = () => {
    const buttons: any[] = [];
    let i = 1;
    if (totalPages && selectedPageNumber) {
      if (totalPages < 6) {
        while (i <= totalPages) {
          buttons.push(
            <PageButton
              onClick={onPageNumberClick}
              value={i}
              selectedPageNumber={selectedPageNumber}
            />
          );

          i++;
        }
      } else {
        if (selectedPageNumber !== 1) {
          buttons.push(
            <PageButton
              onClick={onPageNumberClick}
              value={1}
              selectedPageNumber={selectedPageNumber}
            />
          );
          buttons.push(
            <span className="ml-1 mr-1">...</span>
          );
        }
        buttons.push(
          <PageButton
            onClick={onPageNumberClick}
            value={selectedPageNumber}
            selectedPageNumber={selectedPageNumber}
          />
        );
        if (selectedPageNumber !== totalPages) {
          buttons.push(
            <span className="ml-1 mr-1">...</span>
          );
          buttons.push(
            <PageButton
              onClick={onPageNumberClick}
              value={totalPages}
              selectedPageNumber={selectedPageNumber}
            />
          );
        }
      }
    }

    return buttons;
  };

  const clickNext = () => {
    if (getDataFn && selectedPageNumber) {
      const pageRequest: PageRequest = {
        page: selectedPageNumber,
        sortField: sortedBy,
        sortDirection: sortOrder
      };
      getDataFn(pageRequest);
    }
  };

  const clickPrevious = () => {
    if (getDataFn && selectedPageNumber) {
      const pageRequest: PageRequest = {
        page: selectedPageNumber - 2,
        sortField: sortedBy,
        sortDirection: sortOrder
      };
      getDataFn(pageRequest);
    }
  };

  const onPageNumberClick = (value: number) => {
    if (getDataFn) {
      const pageRequest: PageRequest = {
        page: value - 1,
        sortField: sortedBy,
        sortDirection: sortOrder
      };
      getDataFn(pageRequest);
    }
  };


  const tableElem = (table: any) => (
    <>
      <table className="ReactTable">
        <thead className="-header">
        {composeTableHead(table)}
        </thead>
        <tbody>
        {table.getRowModel().rows.length ? table.getRowModel().rows.map((row: any) => (
            <tr key={row.id} onClick={() => onRowClick(row)}>
              {composeRowCells(row)}
            </tr>
          )) :
          table.getHeaderGroups().map((headerGroup: any) => (
            <>
              <tr key={headerGroup.id}>
                <td className="rt-noData" colSpan={headerGroup.headers.length}>Empty list</td>
              </tr>
            </>
          ))
        }
        </tbody>
      </table>
      {paginationEnabled && totalPages && totalPages > 0 ? (
        <div className="d-flex row margin-top-medium align-items-center ml-1">
          <button disabled={selectedPageNumber === 1}
                  type="button"
                  className="btn btn-outline-light mr-2"
                  onClick={() => clickPrevious()}
          >{t('b.previous').toUpperCase()}
          </button>
          {getPageButtons()}
          <button disabled={selectedPageNumber === totalPages}
                  type="button"
                  className="btn btn-outline-light ml-1 mr-2"
                  onClick={() => clickNext()}
          >{t('b.next').toUpperCase()}
          </button>
          <div>{`Showing ${data.length} of ${totalElements} elements`}</div>
        </div>) : ''
      }

    </>
  );
  return (<>{tableElem(reactTable)}</>);
}
